import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { DEVICE_FAMILY_ALREADY_BOUND_ERROR_CODE } from '../constants/devices.js';
import { normalizeDeviceAlerts } from '../lib/device-alerts.js';
import { type ApiMutationError, toApiMutationError } from '../lib/error.js';
import type { Device } from '../types/device.js';
import type { DeviceFamily } from '../validations/device-form.js';

interface FetchDevicesParams {
  sort?: string;
  tagIds?: string[];
  direction?: 'asc' | 'desc';
  suppressionModes?: string[];
  onlineOnly?: boolean;
  serial?: string;
  page?: number;
  pageSize?: number;
}

interface DevicesPage {
  devices: Device[];
  total: number;
}

// Normalize the OR tag filter: dedupe + sort so an order-insensitive selection
// maps to one stable request URL and React Query cache key (no needless refetch).
const normalizeTagIds = (tagIds?: string[]): string[] =>
  [...new Set((tagIds ?? []).filter(Boolean))].sort();

const fetchDevices = async (params?: FetchDevicesParams): Promise<DevicesPage> => {
  const searchParams = new URLSearchParams();
  if (params?.sort) searchParams.set('sort', params.sort);
  if (params?.direction) searchParams.set('direction', params.direction);
  normalizeTagIds(params?.tagIds).forEach((id) => searchParams.append('tagId', id));
  if (params?.onlineOnly) searchParams.set('onlineOnly', 'true');
  if (params?.serial) searchParams.set('serial', params.serial);
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  params?.suppressionModes?.forEach((mode) => {
    if (mode) searchParams.append('suppressionMode', mode);
  });

  const query = searchParams.toString();
  try {
    const { data } = await apiClient.get<DevicesPage>(`/devices${query ? `?${query}` : ''}`);
    return {
      ...data,
      devices: data.devices.map((device) => ({
        ...device,
        alerts: normalizeDeviceAlerts(device.alerts),
      })),
    };
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch devices');
  }
};

const deleteDevice = async (id: string): Promise<void> => {
  try {
    const { data } = await apiClient.delete(`/devices/${id}`);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to delete device');
  }
};

const rebootDevice = async (id: string): Promise<void> => {
  try {
    await apiClient.post(`/devices/${id}/reboot`);
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to reboot device');
  }
};

const createDevice = async (deviceData: {
  serial: string;
  model?: string;
  firmwareVersion?: string;
  deviceType?: string;
}): Promise<{ device_id: string; token: string }> => {
  try {
    const { data } = await apiClient.post('/devices/register', deviceData);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to create device');
  }
};

const setDeviceFamily = async (params: { id: string; family: DeviceFamily }): Promise<Device> => {
  const { id, family } = params;
  try {
    const { data } = await apiClient.patch<Device>(`/devices/${id}`, { family });
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw toApiMutationError(error, 'Failed to set device family');
  }
};

export const useDevices = (params?: FetchDevicesParams, enabled = true) => {
  const suppressionKey = params?.suppressionModes?.length
    ? params.suppressionModes.join(',')
    : null;
  const normalizedTags = normalizeTagIds(params?.tagIds);
  const tagKey = normalizedTags.length ? normalizedTags.join(',') : null;
  const serial = params?.serial?.trim() ?? '';

  return useQuery({
    queryKey: [
      'devices',
      params?.sort ?? null,
      params?.direction ?? null,
      suppressionKey,
      tagKey,
      params?.onlineOnly ?? null,
      params?.page ?? null,
      params?.pageSize ?? null,
      serial,
    ],
    queryFn: () => fetchDevices({ ...params, serial }),
    staleTime: 60_000, // 1 minute
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    enabled,
  });
};

export const useDeleteDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
};

export const useRebootDevice = () => useMutation({ mutationFn: rebootDevice });

export const useCreateDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
};

export const useSetDeviceFamily = () => {
  const queryClient = useQueryClient();

  const refreshDevice = (id: string) => {
    queryClient.invalidateQueries({ queryKey: ['devices'] });
    queryClient.invalidateQueries({ queryKey: ['device', 'settings', id] });
  };

  return useMutation<Device, ApiMutationError, { id: string; family: DeviceFamily }>({
    mutationFn: setDeviceFamily,
    onSuccess: (boundDevice, variables) => {
      // The open settings panel reads its device from these lists, so it unlocks on this write
      // instead of waiting for the refetch.
      queryClient.setQueriesData<DevicesPage>({ queryKey: ['devices'] }, (page) =>
        page
          ? {
              ...page,
              devices: page.devices.map((device) =>
                device.id === boundDevice.id
                  ? {
                      ...device,
                      ...boundDevice,
                      alerts: normalizeDeviceAlerts(boundDevice.alerts ?? device.alerts),
                    }
                  : device,
              ),
            }
          : page,
      );
      refreshDevice(variables.id);
    },
    onError: (error, variables) => {
      if (error.code === DEVICE_FAMILY_ALREADY_BOUND_ERROR_CODE) refreshDevice(variables.id);
    },
  });
};
